import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { usePro } from '../contexts/ProContext';
import { useWallet } from '../contexts/WalletContext';
import { useTheme } from '../theme';
import { HiOutlineX, HiOutlineCheck, HiOutlineLightningBolt } from 'react-icons/hi';

const PRO_FEATURES = [
  'Multi-protocol wallet analysis (Curve, Aave, Lido, Compound...)',
  'DeFi vs Fixed Income comparison (Selic, T-Bills, ECB rates)',
  'Unlimited email & webhook alerts',
  'APR trend analysis & prediction (SMA, volatility bands)',
  'Performance reports with PDF/CSV export',
  'Smart rebalancing suggestions',
  'Daily portfolio digest by email',
  '90-day APR history (vs 7 days free)',
];

export default function UpgradeModal({ open, onClose }) {
  const { upgrade } = usePro();
  const { isConnected, connect } = useWallet();
  const { colors, dark } = useTheme();
  const [plan, setPlan] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [showUsdc, setShowUsdc] = useState(false);
  const [usdcInfo, setUsdcInfo] = useState(null);

  const handleStripe = async () => {
    if (!isConnected) {
      await connect();
      return;
    }
    setLoading(true);
    try {
      await upgrade('stripe', { plan });
    } catch (err) {
      toast.error(err.message || 'Upgrade failed. Please try again.');
    }
    setLoading(false);
  };

  const handleUsdc = async () => {
    if (!isConnected) {
      await connect();
      return;
    }
    setLoading(true);
    try {
      const info = await upgrade('usdc');
      setUsdcInfo(info);
      setShowUsdc(true);
    } catch (err) {
      toast.error(err.message || 'USDC payment failed. Please try again.');
    }
    setLoading(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={e => e.stopPropagation()}
            style={{
              background: dark ? colors.surface : '#FFFFFF',
              borderRadius: 16,
              border: `1px solid ${colors.border}`,
              boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
              maxWidth: 520,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
            }}
          >
            {/* Header */}
            <div style={{
              padding: '24px 24px 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}>
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 4,
                }}>
                  <span style={{ fontSize: 20 }}>&#x1F451;</span>
                  <span style={{
                    fontFamily: "'DM Serif Display', serif",
                    fontSize: 24,
                    color: colors.textPrimary,
                  }}>
                    PoolScope <span style={{ color: '#8A6A00' }}>Pro</span>
                  </span>
                </div>
                <div style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 12,
                  color: colors.textSecondary,
                }}>
                  Professional DeFi portfolio management
                </div>
              </div>
              <button
                onClick={onClose}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textSecondary,
                  cursor: 'pointer',
                  padding: 4,
                }}
              >
                <HiOutlineX size={20} />
              </button>
            </div>

            {/* Plan toggle */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 4,
              margin: '20px 24px 0',
              background: dark ? 'rgba(255,255,255,0.05)' : '#F4F5F6',
              borderRadius: 10,
              padding: 4,
            }}>
              {[
                { key: 'monthly', label: '$9.90/mo', sub: '' },
                { key: 'yearly', label: '$99/yr', sub: 'Save 17%' },
              ].map(p => (
                <button
                  key={p.key}
                  onClick={() => setPlan(p.key)}
                  style={{
                    flex: 1,
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 13,
                    fontWeight: 600,
                    color: plan === p.key ? '#FFFFFF' : colors.textSecondary,
                    background: plan === p.key ? '#8A6A00' : 'transparent',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {p.label}
                  {p.sub && (
                    <span style={{
                      display: 'block',
                      fontSize: 9,
                      fontWeight: 500,
                      color: plan === p.key ? 'rgba(255,255,255,0.8)' : colors.green,
                      marginTop: 2,
                    }}>
                      {p.sub}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Features */}
            <div style={{ padding: '20px 24px' }}>
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10,
                fontWeight: 600,
                color: colors.labelLight,
                letterSpacing: 1.2,
                marginBottom: 12,
              }}>
                EVERYTHING IN FREE, PLUS:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {PRO_FEATURES.map((f, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 12,
                    color: colors.textPrimary,
                  }}>
                    <HiOutlineCheck size={14} style={{ color: colors.green, flexShrink: 0, marginTop: 1 }} />
                    {f}
                  </div>
                ))}
              </div>
            </div>

            {/* Payment buttons */}
            <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {!showUsdc ? (
                <>
                  <button
                    onClick={handleStripe}
                    disabled={loading}
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 14,
                      fontWeight: 700,
                      color: '#FFFFFF',
                      background: 'linear-gradient(135deg, #8A6A00, #B8941A)',
                      border: 'none',
                      borderRadius: 10,
                      padding: '14px 24px',
                      cursor: loading ? 'default' : 'pointer',
                      opacity: loading ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      letterSpacing: 0.5,
                    }}
                  >
                    <HiOutlineLightningBolt size={16} />
                    {!isConnected ? 'Connect Wallet to Upgrade' : loading ? 'Processing...' : `Upgrade — ${plan === 'yearly' ? '$99/year' : '$9.90/month'}`}
                  </button>
                  <button
                    onClick={handleUsdc}
                    disabled={loading}
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#2775CA',
                      background: dark ? 'rgba(39,117,202,0.1)' : '#EBF3FB',
                      border: `1px solid ${dark ? 'rgba(39,117,202,0.3)' : '#B8D4F0'}`,
                      borderRadius: 10,
                      padding: '12px 24px',
                      cursor: loading ? 'default' : 'pointer',
                      letterSpacing: 0.3,
                    }}
                  >
                    Pay with USDC on Base
                  </button>
                </>
              ) : (
                <div style={{
                  background: dark ? 'rgba(39,117,202,0.08)' : '#F0F7FF',
                  borderRadius: 10,
                  border: `1px solid ${dark ? 'rgba(39,117,202,0.2)' : '#C8DEF5'}`,
                  padding: 16,
                }}>
                  <div style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#2775CA',
                    marginBottom: 12,
                  }}>
                    USDC PAYMENT (BASE NETWORK)
                  </div>
                  <div style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 11,
                    color: colors.textSecondary,
                    lineHeight: 1.6,
                  }}>
                    Send <strong style={{ color: colors.textPrimary }}>{plan === 'yearly' ? '99.00' : '9.90'} USDC</strong> on Base to:
                    <div style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 10,
                      color: colors.textPrimary,
                      background: dark ? 'rgba(0,0,0,0.3)' : '#FFFFFF',
                      padding: '8px 12px',
                      borderRadius: 6,
                      marginTop: 8,
                      wordBreak: 'break-all',
                      border: `1px solid ${colors.border}`,
                    }}>
                      {usdcInfo?.paymentAddress || '...'}
                    </div>
                  </div>
                  <button
                    onClick={() => setShowUsdc(false)}
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 11,
                      color: colors.textSecondary,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      marginTop: 12,
                      padding: 0,
                    }}
                  >
                    &larr; Back to card payment
                  </button>
                </div>
              )}

              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10,
                color: colors.labelMuted,
                textAlign: 'center',
              }}>
                Cancel anytime. No lock-in.
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
